/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";

interface MA600G1Alarm {
  name: string;
  alarmSeverity?: string;
  alarmStat?: string;
  threshold?: string;
}

interface MA600G1RadioModuleAlarmTableProps {
  alarms: MA600G1Alarm[];
  isDeviceDisabled?: boolean;
  selectedModule?: number;
}

const au2CodeToAlarmName: Record<string, string> = {
  "51": "radioModule1ALCOverRangeAlarm",
  "52": "radioModule2ALCOverRangeAlarm",
  "53": "radioModule3ALCOverRangeAlarm",
  "54": "radioModule4ALCOverRangeAlarm",
  "55": "radioModule1Port1DLInputUnderpowerAlarm",
  "56": "radioModule1Port2DLInputUnderpowerAlarm",
  "57": "radioModule1Port3DLInputUnderpowerAlarm",
  "58": "radioModule1Port4DLInputUnderpowerAlarm",
  "59": "radioModule1Port1DLInputOverpowerAlarm",
  "60": "radioModule1Port2DLInputOverpowerAlarm",
  "61": "radioModule1Port3DLInputOverpowerAlarm",
  "62": "radioModule1Port4DLInputOverpowerAlarm",
  "63": "radioModule2Port1DLInputUnderpowerAlarm",
  "64": "radioModule2Port2DLInputUnderpowerAlarm",
  "65": "radioModule2Port3DLInputUnderpowerAlarm",
  "66": "radioModule2Port4DLInputUnderpowerAlarm",
  "67": "radioModule2Port1DLInputOverpowerAlarm",
  "68": "radioModule2Port2DLInputOverpowerAlarm",
  "69": "radioModule2Port3DLInputOverpowerAlarm",
  "70": "radioModule2Port4DLInputOverpowerAlarm",
  "71": "radioModule3Port1DLInputUnderpowerAlarm",
  "72": "radioModule3Port2DLInputUnderpowerAlarm",
  "73": "radioModule3Port3DLInputUnderpowerAlarm",
  "74": "radioModule3Port4DLInputUnderpowerAlarm",
  "75": "radioModule3Port1DLInputOverpowerAlarm",
  "76": "radioModule3Port2DLInputOverpowerAlarm",
  "77": "radioModule3Port3DLInputOverpowerAlarm",
  "78": "radioModule3Port4DLInputOverpowerAlarm",
  "79": "radioModule4Port1DLInputUnderpowerAlarm",
  "80": "radioModule4Port2DLInputUnderpowerAlarm",
  "81": "radioModule4Port3DLInputUnderpowerAlarm",
  "82": "radioModule4Port4DLInputUnderpowerAlarm",
  "83": "radioModule4Port1DLInputOverpowerAlarm",
  "84": "radioModule4Port2DLInputOverpowerAlarm",
  "85": "radioModule4Port3DLInputOverpowerAlarm",
  "86": "radioModule4Port4DLInputOverpowerAlarm",
};

const normalizeAlarmName = (name: string): string => {
  if (!name) return name;
  const coded = name.match(/^MA6200-G(?:1_AU2|2_AU3)_(\d+)$/i);
  if (!coded) return name;
  return au2CodeToAlarmName[coded[1]] || name;
};

/**
 * Get the color for alarm status based on alarmSeverity, alarmStat, and device status
 */
const getAlarmStatusColor = (alarmSeverity?: string, alarmStat?: string, isDeviceDisabled?: boolean): string => {
  if (isDeviceDisabled) {
    return "#9e9e9e"; // gray
  }

  const severity = alarmSeverity ? parseInt(alarmSeverity, 10) : 0;
  const stat = alarmStat ? parseInt(alarmStat, 10) : 0;

  if (severity === 0) {
    return "#9e9e9e"; // gray - disabled
  }

  if (stat === 1) {
    switch (severity) {
      case 2: // warning
        return "#ffc107"; // yellow
      case 3: // minor
        return "#ff9800"; // orange
      case 4: // major
        return "#f44336"; // red
      case 5: // critical
        return "#f44336"; // red
      default:
        return "#4caf50"; // green (normal)
    }
  }

  return "#4caf50"; // green - clear
};

/**
 * Format alarm type name for display
 */
const formatAlarmName = (alarmType: string): string => {
  return alarmType
    .replace(/Port(\d+)/g, 'Port $1 ')
    .replace(/DL/g, 'DL ')
    .replace(/Input/g, 'Input ')
    .replace(/Underpower/g, 'Underpower')
    .replace(/Overpower/g, 'Overpower')
    .replace(/Alarm$/i, '')
    .trim();
};

/**
 * Group alarms by alarm type and port for the selected module
 */
const groupAlarmsByPort = (alarms: MA600G1Alarm[], selectedModule: number): { [key: string]: { [port: number]: MA600G1Alarm } } => {
  const grouped: { [key: string]: { [port: number]: MA600G1Alarm } } = {};
  
  alarms.forEach((alarm) => {
    // Match pattern: radioModule{N}Port{P}{AlarmType}
    const match = alarm.name.match(/radioModule(\d+)Port(\d+)(.*)/i);
    if (match) {
      const moduleNum = parseInt(match[1], 10);
      const portNum = parseInt(match[2], 10);
      const alarmType = match[3];
      
      // Only include alarms for the selected module
      if (moduleNum === selectedModule) {
        if (!grouped[alarmType]) {
          grouped[alarmType] = {};
        }
        grouped[alarmType][portNum] = alarm;
      }
    }
  });
  
  return grouped;
};

/**
 * Render the status/severity/threshold cell for a port
 */
const PortAlarmCell: React.FC<{ alarm?: MA600G1Alarm; isDeviceDisabled?: boolean }> = ({ 
  alarm, 
  isDeviceDisabled 
}) => {
  if (!alarm) {
    return (
      <TableCell className="text-center">
        <span className="text-xs text-muted-foreground">N/A</span>
      </TableCell>
    );
  }

  const statusColor = getAlarmStatusColor(alarm.alarmSeverity, alarm.alarmStat, isDeviceDisabled);

  return (
    <TableCell className="text-center">
      <div className="flex items-center justify-center gap-1.5">
        <div
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: statusColor }}
        />
        {alarm.threshold && (
          <span className="text-xs text-foreground font-medium">{alarm.threshold}</span>
        )}
      </div>
    </TableCell>
  );
};

const MA600G1RadioModuleAlarmTable: React.FC<MA600G1RadioModuleAlarmTableProps> = ({ 
  alarms, 
  isDeviceDisabled = false,
  selectedModule
}) => {
  if (!alarms || alarms.length === 0 || !selectedModule) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">No alarms configured</p>
    );
  }

  const normalizedAlarms = alarms.map((alarm) => ({
    ...alarm,
    name: normalizeAlarmName(alarm.name),
  }));

  const groupedAlarms = groupAlarmsByPort(normalizedAlarms, selectedModule);
  
  // Separate ALC Over Range alarms (module-level, not port-specific)
  const alcAlarmName = `radioModule${selectedModule}ALCOverRangeAlarm`;
  const alcAlarm = normalizedAlarms.find(a => a.name === alcAlarmName);
  
  // Get all alarm types (excluding ALC)
  const alarmTypes = Object.keys(groupedAlarms);

  return (
    <div>
      {/* Main Alarms Table */}
      {alarmTypes.length > 0 && (
        <Table className="mb-2">
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[200px]">Alarm Name</TableHead>
              <TableHead className="text-center w-28">Port 1</TableHead>
              <TableHead className="text-center w-28">Port 2</TableHead>
              <TableHead className="text-center w-28">Port 3</TableHead>
              <TableHead className="text-center w-28">Port 4</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {alarmTypes.sort().map((alarmType, idx) => (
              <TableRow key={`${alarmType}-${idx}`}>
                <TableCell className="font-medium text-xs">{formatAlarmName(alarmType)}</TableCell>
                <PortAlarmCell alarm={groupedAlarms[alarmType][1]} isDeviceDisabled={isDeviceDisabled} />
                <PortAlarmCell alarm={groupedAlarms[alarmType][2]} isDeviceDisabled={isDeviceDisabled} />
                <PortAlarmCell alarm={groupedAlarms[alarmType][3]} isDeviceDisabled={isDeviceDisabled} />
                <PortAlarmCell alarm={groupedAlarms[alarmType][4]} isDeviceDisabled={isDeviceDisabled} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* ALC Over Range - Separate Section (module-level alarm) */}
      {alcAlarm && (() => {
        const statusColor = getAlarmStatusColor(alcAlarm.alarmSeverity, alcAlarm.alarmStat, isDeviceDisabled);
        return (
          <div className="mt-2 p-3 border border-border rounded-lg bg-card flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">ALC Over Range:</span>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColor }} />
              {alcAlarm.threshold && (
                <span className="text-xs text-foreground font-medium">{alcAlarm.threshold}</span>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default MA600G1RadioModuleAlarmTable;
